<template>
  <div :class="{'accordion-list__item-content': advanced}" style="min-width: 0;">
    <h5 class="" v-translate translate-context="event_gallery">Price list</h5>
    <p class="text-help-dark">
      <translate translate-context="event_gallery.subtitle">Define the prices of your photos for sale</translate>
    </p>
    <template v-if="advanced">
      <CRow>
        <CCol lg="7">
          <h6 class="font-weight-semi-bold">{{ $pgettext('event_wizard.price_list.form', 'Display name') }}</h6>
          <CInput v-model="form.display_name" @input="onPreview"/>
        </CCol>
        <CCol lg="5">
          <h6 class="font-weight-semi-bold">{{ $pgettext('event_wizard.price_list.form', 'Validity Period') }}</h6>
          <VueCtkDateTimePicker
            v-model="validity"
            :no-label="true"
            :label="$gettext('Select date')"
            format="YYYY-MM-DD HH:mm:ss"
            formatted="LL"
            range
            :right="true"
            :only-date="true"
            :no-button-now="true"
            color="#1867FF"
            @input="onPreview"
          />
        </CCol>
      </CRow>
    </template>
    <h6 class="font-weight-semi-bold" v-translate translate-context="event_gallery.price_list">
      Price list type
    </h6>
    <p class="text-help-dark">
      <translate translate-context="event_gallery.price_list.subtitle">Set the selling price of your photos</translate>
    </p>
    <div style="position: relative; height: 200px;">
      <div class="form-option-w" style="">
        <div class="form-option"
             v-for="typeOption in cardOptions" :key="typeOption.id"
             @click.prevent.stop="onSelectType(typeOption.id)"
             :class="{'active': typeOption.id === form.configuration.type}">
          <div class="title">
            <CIcon :name="typeOption.icon" size="custom-size" :height="20" class="mb-2"/>
            <span class="name">{{ typeOption.name }}</span>
          </div>
          <div class="descr">{{ typeOption.description }}</div>
        </div>
      </div>
    </div>
    <PackPriceList
      :currency="eventCurrency"
      :form_in_line="advanced"
      @update="onPreviewPriceWithSettings"
      ref="prices"
      v-if="form.configuration.type === priceListType.pack"
    />
    <UnitPriceList
      :currency="eventCurrency"
      :form_in_line="advanced"
      @update="onPreviewPriceWithSettings"
      ref="prices"
      v-if="form.configuration.type === priceListType.unit"
    />
    <CustomPriceList
      :currency="eventCurrency"
      @update="onPreviewPriceWithSettings"
      ref="prices"
      v-if="form.configuration.type === priceListType.custom"
    />
    <ParentPriceList
      ref="prices"
      @update="onPreviewPrice"
      v-if="form.configuration.type === priceListType.parent"
    />
    <TemplatePriceList
      ref="prices"
      @update="onPreviewPrice"
      v-if="form.configuration.type === priceListType.template"
    />
  </div>
</template>

<script>

import 'vue2-timepicker/dist/VueTimepicker.css'
import CustomPriceList from "@/domain/eventGallery/tab/priceList/CustomPriceList.vue";
import priceListType from "@/domain/core/constant/priceListType";
import PackPriceList from "@/domain/eventGallery/tab/priceList/PackPriceList.vue";
import UnitPriceList from "@/domain/eventGallery/tab/priceList/UnitPriceList.vue";
import ParentPriceList from "@/domain/eventGallery/tab/priceList/ParentPriceList.vue";
import {mapGetters} from "vuex";
import TemplatePriceList from "@/domain/eventGallery/tab/priceList/TemplatePriceList.vue";

export default {
  name: "PriceListOpened",
  emits: ['update', 'preview'],
  components: {
    TemplatePriceList,
    ParentPriceList,
    UnitPriceList,
    PackPriceList,
    CustomPriceList
  },
  data() {
    return {
      validity: {
        start: null,
        end: null,
      },
      form: {
        items: [],
        configuration: {},
        valid_from: null,
        valid_to: null,
        allow_preorder: false,
        allow_upsell: false,
        cta_enabled: false,
        cta_min_qty: 0,
        cta_position: 'b',
        display_name: '',
      },
      ctaPositions: [
        {id: 'b', name: this.$pgettext('event_wizard.price_list.form.label', 'Bottom')},
        {id: 't', name: this.$pgettext('event_wizard.price_list.form.label', 'Top')},
      ],
    }
  },
  watch: {
    data: function () {
      this.onChangeData()
    }
  },
  mounted() {
    this.onChangeData()
  },
  computed: {
    ...mapGetters('event', ['eventCurrency']),
    priceListType() {
      return priceListType
    },
    cardOptions() {
      let options = [
        {
          id: 'unit',
          name: this.$pgettext('event_gallery.price_list.type', 'Unit price'),
          description: this.$pgettext('event_gallery.price_list.type', 'Set the selling price for each single photo'),
          icon: 'cipPicture'
        },
        {
          id: 'pack',
          name: this.$pgettext('event_gallery.price_list.type', 'Complete package'),
          description: this.$pgettext('event_gallery.price_list.type', 'Select the number of photos in the package. Price per photo calculated automatically.'),
          icon: 'cipLayersThree01'
        },
      ]
      if (this.isSubGallery) {
        options.push({
          id: 'parent',
          name: this.$pgettext('event_gallery.price_list.type', 'Parent'),
          description: this.$pgettext('event_gallery.price_list.type', 'Use parent gallery price list'),
          icon: 'cipLayersThree01'
        })
      }
      if (this.advanced) {
        options.push({
          id: 'custom',
          name: this.$pgettext('event_gallery.price_list.type', 'Custom'),
          description: this.$pgettext('event_gallery.price_list.type', 'Create a fully custom price list'),
          icon: 'cipPencil'
        })
        options.push({
          id: 'template',
          name: this.$pgettext('event_gallery.price_list.type', 'Template'),
          description: this.$pgettext('event_gallery.price_list.type', 'Use a predefined price list template'),
          icon: 'cipWindowCode'
        })
      }
      return options
    }
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    advanced: {
      type: Boolean,
      required: true
    },
    isSubGallery: {
      type: Boolean,
      required: true
    }
  },
  methods: {
    onSelectType(newType) {
      if (newType === this.form.configuration.type) return
      this.onChangeLayoutType({value: newType})
    },
    isValid() {
      if (!this.$refs.prices) return false
      if (typeof this.$refs.prices.isValid === 'function') return this.$refs.prices.isValid()
      return true
    },
    getPriceList() {
      const priceList = this.$refs.prices.getPriceList()
      this.form.items = priceList.items
      this.form.configuration = {
        ...priceList.configuration,
        type: this.form.configuration.type
      }

      // Se il componente Custom restituisce i settings, li aggiorna
      if (priceList.settings) {
        this.form.allow_preorder = priceList.settings.allow_preorder
        this.form.allow_upsell = priceList.settings.allow_upsell
        this.form.cta_enabled = priceList.settings.cta_enabled
        this.form.cta_min_qty = priceList.settings.cta_min_qty
        this.form.cta_position = priceList.settings.cta_position
      }

      return {
        ...this.form,
        valid_from: this.validity.start,
        valid_to: this.validity.end,
      }
    },
    async onChangeData() {
      this.form = {
        ...this.data,
      }
      if (!this.form.configuration)
        this.form.configuration = {type: priceListType.default(this.isSubGallery)}
      if (!this.form.configuration.type)
        this.form.configuration.type = priceListType.default(this.isSubGallery)

      this.validity = {
        start: this.data.valid_from,
        end: this.data.valid_to,
      }

      await this.initPrices()
    },
    onPreviewPrice({items, configuration}) {
      this.form.items = items
      this.form.configuration = {
        ...configuration,
        type: this.form.configuration.type
      }
      this.onPreview()
    },
    onPreviewPriceWithSettings({items, configuration, settings}) {
      this.form.items = items
      this.form.configuration = {
        ...configuration,
        type: this.form.configuration.type
      }
      // Aggiorna i settings dal componente Custom
      if (settings) {
        this.form.allow_preorder = settings.allow_preorder
        this.form.allow_upsell = settings.allow_upsell
        this.form.cta_enabled = settings.cta_enabled
        this.form.cta_min_qty = settings.cta_min_qty
        this.form.cta_position = settings.cta_position
      }
      this.onPreview()
    },
    async onPreview() {
      this.$emit('preview', {
        ...this.form,
        valid_from: this.validity.start,
        valid_to: this.validity.end,
      })
    },
    async onChangeLayoutType(newTypeObj) {
      const newType = newTypeObj.value

      if (newType === priceListType.custom) {
        this.form.configuration = {
          type: newType
        }
        await this.initPrices(true)
        return this.onPreview()
      }
      let confirmMsg = this.$gettext('Changing the price list type will reset every unsaved parameters, are you sure?')
      let isEmpty = !this.form.items || this.form.items.length === 0
      if (isEmpty || confirm(confirmMsg)) {
        this.form.items = []
        this.form.configuration = {
          type: newType
        }
        await this.initPrices(true)
        this.onPreview()
      }
    },
    async initPrices(onChange = false) {
      await this.$nextTick()
      await this.delay(100)
      if (!this.$refs.prices) return

      const initData = {
        items: this.form.items,
        configuration: this.form.configuration,
        onChange: onChange
      }

      // Passa i settings correnti solo al componente Custom
      if (this.form.configuration.type === priceListType.custom) {
        initData.parentSettings = {
          allow_preorder: this.form.allow_preorder,
          allow_upsell: this.form.allow_upsell,
          cta_enabled: this.form.cta_enabled,
          cta_min_qty: this.form.cta_min_qty,
          cta_position: this.form.cta_position
        }
      }

      this.$refs.prices.init(initData)
    }
  }
}
</script>